<script setup lang="ts">
import type { ColorPair } from '~/models/Presentation/BasePage'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref, watch } from 'vue'
import { faPause, faPlay } from '@fortawesome/pro-solid-svg-icons'
import { useLottie } from '~/composables/useLottie'
import Button from '~/components/ui/Button.vue'

const props = withDefaults(defineProps<{
  contentId: number
  autoPlay?: boolean
  pauseAnimation?: boolean
  colorPair?: ColorPair
}>(), {
  autoPlay: !matchMedia('(prefers-reduced-motion)').matches,
  pauseAnimation: false
})

const { lottie, lottieJSON, getLottie } = useLottie()
const { t } = useI18n()

const lottieAnimation = ref()
const isPlaying = ref(props.autoPlay)
const showButton = ref(false)
const buttonHasFocus = ref(false)

const playOrPause = () => {
  if (!props.autoPlay)
    return

  showButton.value = true
  setTimeout(() => showButton.value = false, 500)

  isPlaying.value
    ? lottieAnimation.value.pause()
    : lottieAnimation.value.play()

  isPlaying.value = !isPlaying.value
}

const iconLeft = computed(() => {
  if ((buttonHasFocus.value && isPlaying.value) || (!buttonHasFocus.value && !isPlaying.value))
    return faPause
  if ((buttonHasFocus.value && !isPlaying.value) || (!buttonHasFocus.value && isPlaying.value))
    return faPlay
  return ''
})

// This line updates the isPlaying value when animations are paused in ThreeStepPage.vue
watch(() => props.pauseAnimation, () => isPlaying.value = !props.pauseAnimation)

onMounted(() => props.contentId && getLottie(props.contentId))
</script>

<template>
  <div
    v-if="lottieJSON"
    class="relative h-full w-full"
  >
    <p
      :id="`lottie-${contentId}`"
      class="hidden"
      v-text="lottie?.alternativeText"
    />
    <LottieAnimation
      ref="lottieAnimation"
      :animation-data="lottieJSON"
      :auto-play="autoPlay"
      :pause-animation="pauseAnimation"
      :aria-labelledby="`lottie-${contentId}`"
      role="img"
      width="100%"
      @click="playOrPause"
    />
    <div
      class="absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transition-all duration-300"
      :class="showButton || buttonHasFocus ? 'scale-100 opacity-100' : 'scale-0 opacity-0'"
    >
      <Button
        shape="square"
        class="pointer-events-none"
        size="lg"
        :aria-label="isPlaying ? t('lottie.pause') : t('lottie.play')"
        :style="colorPair && { background: colorPair.background, color: colorPair.text }"
        @click="playOrPause"
        @focusin="buttonHasFocus = true"
        @focusout="buttonHasFocus = false"
      >
        <font-awesome-icon :icon="iconLeft" />
      </Button>
    </div>
  </div>
</template>
